import { create } from 'zustand'
import { supabase } from './supabase'
import type { Workspace } from './supabase'
import type { User } from '@supabase/supabase-js'
import { sendEmail } from './email'
import { renderWelcomeEmail } from './welcomeEmail'
import { queryClient } from './queryClient'
import { Sentry } from './sentry'

interface Profile {
  id: string
  workspace_id: string
  full_name: string
  avatar_url?: string
  role: string
  is_owner: boolean
}

// Portal-user summary populated when the authenticated user is a portal
// user (no profiles row, has a portal_users row). The presence of
// portalUser is the signal route guards use to redirect between /app
// and /portal. Staff sessions always have portalUser=null.
export interface PortalUserSummary {
  id: string
  workspace_id: string
  company_id: string | null
  contact_id: string
  role: 'admin' | 'member' | 'viewer'
}

// Vendor-user summary — the third citizen. Mirrors PortalUserSummary's
// purpose: populated for vendor-portal sessions (no profiles row, no
// portal_users row, has a vendor_users row). The three flags are
// mutually exclusive — a single auth.users row can be exactly one of
// staff (profile) / client (portalUser) / vendor (vendorUser). V1
// foundation: no vendor UI yet; the flag exists so route guards can
// already isolate a vendor session from /app and /portal.
export interface VendorUserSummary {
  id: string
  workspace_id: string
  vendor_id: string
  role: 'admin' | 'tech'
}

interface AuthState {
  user: User | null
  profile: Profile | null
  workspace: Workspace | null
  portalUser: PortalUserSummary | null
  vendorUser: VendorUserSummary | null
  loading: boolean
  initialized: boolean
  // Set true only AFTER initialize()'s session restore has run to completion
  // (with user populated or definitively absent). Distinct from `initialized`,
  // which flips synchronously at initialize() entry as the re-entry guard for
  // StrictMode double-mount (Session 33 fix at 68f4d65 — DO NOT conflate).
  // Route guards must gate on this, not on `initialized`, or they fire the
  // !user → /signin redirect during the still-hydrating-session window.
  sessionResolved: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, name: string, workspaceName: string) => Promise<{ error: Error | null; needsConfirmation?: boolean }>
  signOut: () => Promise<void>
  initialize: () => Promise<{ unsubscribe: () => void } | undefined>
  refreshWorkspace: () => Promise<void>
}

// Prevents onAuthStateChange from running loadUserData concurrently during signUp,
// which would race against workspace/profile creation.
let signingUp = false

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  workspace: null,
  portalUser: null,
  vendorUser: null,
  loading: false,
  initialized: false,
  sessionResolved: false,

  initialize: async () => {
    if (get().initialized) return
    set({ loading: true, initialized: true })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        await loadUserData(session.user, set)
      }
    } catch (error) {
      console.error('Init error:', error)
    } finally {
      // sessionResolved flips true here — AFTER getSession + loadUserData have
      // either populated `user` or definitively confirmed no session. Route
      // guards in App.tsx wait on this, so the !user → /signin redirect can no
      // longer fire during the hydrating-session window. Logged-out path: this
      // still runs (getSession returned null, loadUserData was skipped), user
      // stays null, guards correctly redirect to /signin once the screen
      // re-renders.
      set({ loading: false, sessionResolved: true })
    }

    // Listen for auth changes — skip SIGNED_IN during signUp to avoid racing
    // against workspace/profile creation (signUp calls loadUserData explicitly).
    //
    // Bridges every auth transition Supabase emits to the React Query cache so
    // cached responses keyed against an old session don't survive into a new
    // one. Closes the cache-refresh symptom (a) where gates required a hard
    // refresh after sign-out/in across plan-tier or RLS changes (S17-A).
    //
    // The handler body must NOT await any supabase.from(...) or other
    // operation that itself acquires GoTrueClient's internal lock — the
    // handler runs inside _acquireLock's callback, and re-entry causes
    // a deadlock (pendingInLock grows unboundedly, queries hang). Async
    // work in branches below is deferred via setTimeout(0) so it runs
    // after the outer lock releases. See dist/module/GoTrueClient.js:2237-2289
    // and supabase-js issues #2013, #2111, #1594.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.info('[auth]', event, {
        timestamp: new Date().toISOString(),
        hasSession: !!session,
        userId: session?.user?.id,
        expiresAt: session?.expires_at
          ? new Date(session.expires_at * 1000).toISOString()
          : null,
      })
      if (event === 'SIGNED_IN' && session?.user) {
        if (signingUp) return
        const user = session.user
        setTimeout(() => {
          loadUserData(user, set).then(() => queryClient.invalidateQueries())
        }, 0)
      } else if (event === 'SIGNED_OUT') {
        set({ user: null, profile: null, workspace: null, portalUser: null, vendorUser: null })
        queryClient.clear()
        Sentry.setUser(null)
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        const user = session.user
        setTimeout(() => {
          loadUserData(user, set)
        }, 0)
      } else if (event === 'USER_UPDATED' && session?.user) {
        const user = session.user
        setTimeout(() => {
          loadUserData(user, set)
        }, 0)
      }
    })

    return subscription
  },

  signIn: async (email, password) => {
    set({ loading: true })
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      if (data.user) await loadUserData(data.user, set)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    } finally {
      set({ loading: false })
    }
  },

  signUp: async (email, password, name, workspaceName) => {
    set({ loading: true })
    signingUp = true
    try {
      // Stash name + workspace_name into user_metadata so they survive the
      // email-confirmation redirect: when confirmation is ON, supabase.auth.signUp
      // returns no session, the user clicks the email link, and the post-confirmation
      // SIGNED_IN handler reads these values in loadUserData() to run the RPC then.
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: name, workspace_name: workspaceName } },
      })
      if (error) throw error
      if (!data.user) throw new Error('Sign-up failed — no user was returned.')

      // Confirmation-ON path: no session yet. Defer workspace creation until
      // the user returns authenticated via the email link; loadUserData() picks
      // it up from user_metadata. Return a flag so the form shows "check your
      // email" instead of navigating to /onboarding (the onboarding page assumes
      // a workspace already exists and would just spin a LoadingScreen otherwise).
      if (!data.session) {
        return { error: null, needsConfirmation: true }
      }

      // Confirmation-OFF path (production today): we have a session, create the
      // workspace inline as before — behavior unchanged from the prior implementation
      // aside from the metadata stash above and the welcome-email helper extraction.
      const { error: rpcError } = await supabase.rpc('signup_with_workspace', {
        p_user_id:        data.user.id,
        p_full_name:      name,
        p_workspace_name: workspaceName,
      })
      if (rpcError) throw new Error(`Failed to set up workspace: ${rpcError.message}`)

      fireWelcomeEmail(email, name, workspaceName)

      // Load user + profile + workspace atomically now that all rows exist.
      await loadUserData(data.user, set)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    } finally {
      signingUp = false
      set({ loading: false })
    }
  },

  signOut: async () => {
    await supabase.auth.signOut()
    set({ user: null, profile: null, workspace: null })
  },

  refreshWorkspace: async () => {
    const { user } = get()
    if (!user) return
    const { data: profile } = await supabase
      .from('profiles')
      .select('*, workspaces(*)')
      .eq('id', user.id)
      .maybeSingle()
    if (profile) {
      const { workspaces, ...profileData } = profile as any
      set({ profile: profileData, workspace: workspaces })
    }
  },
}))

// Dev-mode observability — expose state for runtime inspection during the
// chronic intermittent stuck-loading investigation (Session 28 banked, Session
// 33 partial closure). Console-accessible: window.useAuth.getState() and
// window.supabase.auth.getSession(). Active in local dev AND on
// dev.orkestrapro.com (Kris's daily-driver surface where the bug recurs); the
// hostname check excludes any future real-production hostname automatically.
const isDiagnosticContext =
  import.meta.env.DEV ||
  (typeof window !== 'undefined' &&
   window.location.hostname === 'dev.orkestrapro.com')
if (isDiagnosticContext) {
  if (typeof window !== 'undefined') {
    ;(window as any).useAuth = useAuth
    ;(window as any).supabase = supabase
  }
}

// Fire-and-forget welcome email. Used by both signup paths — the inline path
// in signUp() (confirmation-OFF) and the post-confirmation repair branch in
// loadUserData() (confirmation-ON). Errors are logged but never thrown; the
// UI must never hang waiting for email delivery.
function fireWelcomeEmail(toEmail: string, name: string, workspaceName: string) {
  if (!toEmail) return
  ;(async () => {
    try {
      const { subject, html } = renderWelcomeEmail({ name, workspaceName })
      const { error: emailError } = await sendEmail({
        to: toEmail,
        subject,
        html,
        // Welcome emails go to Kris's inbox, not the new user's own inbox —
        // the whole point is for replies to reach a human. Hardcoded for now;
        // when kris@orkestrapro.com inbox is set up, swap to that.
        replyTo: 'krisk58504@gmail.com',
      })
      if (emailError) {
        console.error('[signup] welcome email failed:', emailError)
      }
    } catch (err) {
      console.error('[signup] welcome email threw:', err)
    }
  })()
}

async function loadUserData(user: User, set: any) {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('*, workspaces(*)')
      .eq('id', user.id)
      .maybeSingle()

    if (profile) {
      const { workspaces, ...profileData } = profile as any
      // Single atomic update — no window where user is set but workspace isn't.
      set({ user, profile: profileData, workspace: workspaces, portalUser: null, vendorUser: null })
      // Tag Sentry events with the workspace UUID so production errors can be
      // correlated to a specific tenant. Workspace ID only — no email or name
      // crosses the PII boundary (see src/lib/sentry.ts).
      if (workspaces?.id) {
        Sentry.setUser({ id: workspaces.id })
      }
      return
    }

    // No profile row. Before treating this as a stale/incomplete staff
    // session, check whether the session belongs to a PORTAL user
    // (clients invited via portal-invite have an auth.users row + a
    // portal_users row but NO profiles row, by design). Portal sessions
    // are valid — they just route to /portal/* instead of /app/*.
    // App.tsx's ProtectedRoute redirects to /portal when portalUser is
    // set, and the PortalGuard requires portalUser to enter /portal.
    const { data: portalUserRow } = await supabase
      .from('portal_users')
      .select('id, workspace_id, company_id, contact_id, role')
      .eq('auth_user_id', user.id)
      .maybeSingle()
    if (portalUserRow) {
      set({ user, profile: null, workspace: null, portalUser: portalUserRow as PortalUserSummary, vendorUser: null })
      // Best-effort last_login_at touch — fire-and-forget; failing this
      // doesn't matter to the session.
      supabase.from('portal_users').update({ last_login_at: new Date().toISOString() })
        .eq('id', portalUserRow.id)
        .then(() => undefined, () => undefined)
      return
    }

    // Third citizen: vendor portal session. Same shape — vendor_users
    // row but no profile, no portal_users row. Route guards in App.tsx
    // treat vendorUser as the signal to isolate from /app and /portal.
    // V1 has no /vendor/* home for an authenticated vendor session yet;
    // the guards bounce vendor sessions to /signin until the V2 surfaces
    // land. (vendor-respond's token URL flow at /vendor/assignment/*
    // is unaffected — those routes are public and don't require a
    // session.)
    const { data: vendorUserRow } = await supabase
      .from('vendor_users')
      .select('id, workspace_id, vendor_id, role')
      .eq('auth_user_id', user.id)
      .maybeSingle()
    if (vendorUserRow) {
      set({ user, profile: null, workspace: null, portalUser: null, vendorUser: vendorUserRow as VendorUserSummary })
      supabase.from('vendor_users').update({ last_login_at: new Date().toISOString() })
        .eq('id', vendorUserRow.id)
        .then(() => undefined, () => undefined)
      return
    }

    // Profile not found (maybeSingle returns null on 0 rows). Two paths:
    //  (a) Fresh signup awaiting workspace creation — user_metadata still
    //      carries workspace_name from signUp(). This is the post-email-
    //      confirmation first sign-in (confirmation-ON path): create the
    //      workspace via the RPC the inline path uses, then re-query.
    //  (b) Stale session — the profile this session refers to has been
    //      deleted (e.g. demo workspace wipe+reseed assigned new UUIDs to
    //      the demo users). The session is unrecoverable. Sign out so the
    //      SIGNED_OUT handler clears state and ProtectedRoute redirects to
    //      /signin, rather than landing on AppLayout with workspace=null.
    const meta = user.user_metadata ?? {}
    if (typeof meta.full_name === 'string' && typeof meta.workspace_name === 'string') {
      const { error: rpcError } = await supabase.rpc('signup_with_workspace', {
        p_user_id:        user.id,
        p_full_name:      meta.full_name,
        p_workspace_name: meta.workspace_name,
      })
      if (rpcError) {
        console.error('[auth] post-confirmation workspace creation failed', {
          error: rpcError,
          userId: user.id,
        })
        set({ user })
        return
      }
      fireWelcomeEmail(user.email ?? '', meta.full_name, meta.workspace_name)
      // Re-query now that the workspace exists.
      const { data: profile2 } = await supabase
        .from('profiles')
        .select('*, workspaces(*)')
        .eq('id', user.id)
        .maybeSingle()
      if (profile2) {
        const { workspaces, ...profileData } = profile2 as any
        set({ user, profile: profileData, workspace: workspaces, portalUser: null, vendorUser: null })
        if (workspaces?.id) {
          Sentry.setUser({ id: workspaces.id })
        }
        return
      }
      // Repair RPC succeeded but the re-query found no profile. Highly
      // unexpected — fall through to sign-out below rather than leave the
      // user in the workspace=null stuck state.
    }

    // Definitive no-profile (no fresh-signup metadata OR repair didn't
    // resolve). Sign out so the session is invalidated and the next render
    // hits /signin via ProtectedRoute's !user branch.
    console.warn('[auth] no profile for session, signing out', { userId: user.id })
    await supabase.auth.signOut()
    // SIGNED_OUT handler (above) clears { user, profile, workspace }.
    // Intentionally do NOT set({ user }) here — that would re-establish the
    // stuck state we're trying to break out of.
  } catch (error) {
    // Session 28 finding F1: this catch silently leaves workspace null, after
    // which ProtectedRoute renders children with all queries disabled. Log full
    // context so we can tell if this branch is firing during the chronic
    // stuck-loading symptom.
    console.error('[auth] loadUserData failed', {
      error,
      userId: user?.id,
      timestamp: new Date().toISOString(),
      stack: error instanceof Error ? error.stack : undefined,
    })
    set({ user })
  }
}
